// LiveAgentBotSettings/index.js - Updated version

import React, { useEffect, useState } from "react";
import AlertPopup from "../../../AlertPopup";
import LoaderComp from "../../../LoaderComp";
import styles from "../LeadGenBotSettings/BotSettings.module.css";
import LeadGenBotSettingStyle from "./LiveAgentBotSettings.module.css";
import ThemeCustomizer from "../../../NewCreateAppF/ThemeCustomizer";
import LiveBot from "../../../app_components/LiveBot";
import Modal from "../../../Modal";
import modalContentSty from "../LeadGenBotSettings/ModalContent.module.css";
import { withPreviewRoot } from "../../../../hoc/withPreviewRoot";

const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

const getToken = () => localStorage.getItem("token");

const CORE_KEYS = [
  "app_name",
  "ai_agent_bot_enabled",
  "dont_show_chat_box_at_logout",
  "exclude_paths",
  "live_agent_enabled",
  "allow_nested",
  "app_id",
  "auth_key",
  "chat_settings",
  "enabled_bot_content",
  "feature",
  "region",
  "role",
  "settings",
  "tenant_id",
  "timestamp",
  "tenant_info",
  "isTraining",
  "session_enabled",
  "live_settings",
  // ⭐ NEW: admin-configurable queue thresholds for the live-agent
  //    wait flow. These are read by the client SDK to decide when
  //    to give up waiting and show "all agents busy."
  "pending_timeout_in_minute",
  "tenant_load_threshold",
  "guest_live_chat_enabled",
  // ⭐ NEW: per-app identity storage backend config.
  "identity_storage",
];

// Defaults used when the app data doesn't yet contain the new fields
// (e.g., tenants that predate this admin release).
const LIVE_AGENT_DEFAULTS = {
  pending_timeout_in_minute: 2,
  tenant_load_threshold: 5,
  guest_live_chat_enabled: false,

  // ⭐ Video-call defaults. Both on, matching the pre-flag behavior
  //    where the widget always requested camera + mic.
  //
  //    Shape: { audio: boolean, video: boolean }
  //
  //    Stored inside live_settings so it rides along with the
  //    existing save pipeline (live_settings is already in CORE_KEYS
  //    and gets persisted wholesale on every Live Agent save).
  video_setting: { audio: true, video: true },

  // ⭐ Identity storage backend defaults.
  //
  //    Controls which browser storage the client SDK uses to
  //    persist each identity type:
  //
  //      "session" → sessionStorage. Wiped when the tab closes.
  //      "local"   → localStorage.  Persists across restarts.
  //
  //    Defaults:
  //      visitor:  "session" — guests disappear with the tab.
  //      customer: "local"   — real users stay logged in.
  //
  //    Shape: { visitor: "session" | "local", customer: "session" | "local" }
  //    Stored as a top-level app-data field.
  identity_storage: { visitor: "session", customer: "local" },
};

// ⭐ Validation ranges for the two threshold fields.
const PENDING_TIMEOUT_MIN = 1;
const PENDING_TIMEOUT_MAX = 30;
const TENANT_LOAD_THRESHOLD_MIN = 1;
const TENANT_LOAD_THRESHOLD_MAX = 50;

// Advanced Chat Modal Opener Styles - For preview only
const CHAT_MODAL_STYLES = {
  default: {
    name: "Default Style",
    previewHtml: `<div style="background-color: rgb(163, 112, 206); display: inline-flex; align-items: center; justify-content: center; width: 50px; height: 50px; border-radius: 50%; position: relative; margin: 0 auto;"><i class="fas fa-comments" style="color: rgb(255, 255, 255); font-size: 24px;"></i><div style="position: absolute; top: -5px; right: -5px; background-color: red; color: rgb(255, 255, 255); border-radius: 50%; padding: 5px 10px; font-size: 12px;">0</div></div>`,
  },
  gradient_pulse: {
    name: "Gradient Pulse",
    previewHtml: `<div style="display: inline-flex; align-items: center; justify-content: center; width: 50px; height: 50px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 50%; cursor: pointer; box-shadow: 0 4px 15px rgba(0,0,0,0.2); transition: all 0.3s ease; animation: pulse 2s infinite; position: relative; margin: 0 auto;"><i class="fas fa-comment-dots" style="color: white; font-size: 24px;"></i><div style="position: absolute; top: -5px; right: -5px; background-color: #ff4757; color: white; border-radius: 50%; min-width: 22px; height: 22px; padding: 0 6px; font-size: 12px; font-weight: bold; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.2);">0</div><style>@keyframes pulse { 0% { transform: scale(1); box-shadow: 0 4px 15px rgba(0,0,0,0.2); } 50% { transform: scale(1.05); box-shadow: 0 6px 20px rgba(102,126,234,0.4); } 100% { transform: scale(1); box-shadow: 0 4px 15px rgba(0,0,0,0.2); } }</style></div>`,
  },
  neomorphic_glass: {
    name: "Neomorphic Glass",
    previewHtml: `<div style="display: inline-flex; align-items: center; justify-content: center; width: 55px; height: 55px; background: rgba(255,255,255,0.25); backdrop-filter: blur(10px); border-radius: 15px; cursor: pointer; box-shadow: 8px 8px 16px rgba(0,0,0,0.1), -8px -8px 16px rgba(255,255,255,0.5); transition: all 0.3s ease; border: 1px solid rgba(255,255,255,0.3); position: relative; margin: 0 auto;"><i class="fas fa-headset" style="color: #5f27cd; font-size: 26px; text-shadow: 2px 2px 4px rgba(0,0,0,0.1);"></i><div style="position: absolute; top: -8px; right: -8px; background: linear-gradient(135deg, #ff6b6b, #ee5a24); color: white; border-radius: 50%; min-width: 22px; height: 22px; padding: 0 6px; font-size: 11px; font-weight: bold; display: flex; align-items: center; justify-content: center; border: 2px solid rgba(255,255,255,0.8); box-shadow: 0 2px 10px rgba(0,0,0,0.2);">0</div><style>.neo-glass-preview:hover { transform: translateY(-3px); }</style></div>`,
  },
};

function LiveAgentBotSettings({ appData, tenantPlan, onAppDataPatch }) {
  console.log("appDataddishere?dappData", JSON.stringify(appData));
  const [initialData, setInitialData] = useState(null);
  const [botContent, setBotContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const [localChatSettings, setLocalChatSettings] = useState(
    appData.chat_settings,
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingUpdatePayloads, setPendingUpdatePayloads] = useState({
    payload1: null,
  });
  const [selectedChatStyle, setSelectedChatStyle] = useState(() => {
    // Load saved style from chat_settings or default to 'default'
    return appData.chat_settings?.chat_opener_style || "default";
  });

  // Function to sync chat settings back to botContent
  const handleChatSettingsChange = (updatedChatSettings) => {
    console.log("Updating chat settings:", updatedChatSettings);
    setLocalChatSettings(updatedChatSettings);

    // Sync to botContent
    setBotContent((prev) => ({
      ...prev,
      chat_settings: updatedChatSettings,
    }));
  };

  // Handle chat style change - ONLY saves the style flag, no HTML
  const handleChatStyleChange = (styleKey) => {
    setSelectedChatStyle(styleKey);

    // Update chat_settings with ONLY the style flag
    const updatedChatSettings = {
      ...localChatSettings,
      chat_opener_style: styleKey,
      // NOT saving any HTML here
    };

    handleChatSettingsChange(updatedChatSettings);
  };

  // ⭐ NEW: input handlers for the two live-agent threshold fields.
  //
  // Both fields are numeric and get clamped into their allowed
  // ranges at the time of typing. Invalid input (empty, NaN) is
  // silently ignored so the previous value stays in place until
  // the user types a real number.
  const handleNumericFieldChange = (key, rawValue, min, max) => {
    if (rawValue === "") return;
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) return;
    const clamped = Math.min(Math.max(Math.floor(parsed), min), max);
    setBotContent((prev) => ({ ...prev, [key]: clamped }));
  };

  const hasChanges = () => {
    if (!initialData || !botContent) return false;

    const coreChanged = CORE_KEYS.some((key) => {
      if (key === "chat_settings") return false;
      if (
        initialData.botContent[key] === undefined ||
        botContent[key] === undefined
      ) {
        return false;
      }
      return (
        JSON.stringify(initialData.botContent[key]) !==
        JSON.stringify(botContent[key])
      );
    });

    // Compare chat_settings from initialData and current botContent
    const chatSettingsChanged =
      JSON.stringify(initialData.chatSettings) !==
      JSON.stringify(botContent.chat_settings);

    return coreChanged || chatSettingsChanged;
  };

  const confirmUpdate = async (confirmed) => {
    setShowConfirmModal(false);

    if (!confirmed) {
      showAlert("info", "Update cancelled.");
      return;
    }

    const { payload1 } = pendingUpdatePayloads;

    if (!payload1) {
      showAlert("error", "Update data not found");
      return;
    }

    setUpdating(true);

    try {
      const region_prefix = appData.region.toLowerCase();
      const token = getToken();
      const updateAppResponse = await fetch(
        `https://${region_prefix}.apps2.${backend_api_base_url}/update_app?act_type=tenant&app_name=${appData.app_name}`,
        {
          method: "PUT",
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload1),
        },
      );

      const updateAppData = await updateAppResponse.json();

      if (!updateAppResponse.ok) {
        showAlert("error", "Failed to update Live Agent configuration");
        setUpdating(false);
        return;
      }

      // Update initial data to match current state
      setInitialData({
        botContent: { ...botContent },
        chatSettings: { ...botContent.chat_settings },
      });

      // ─────────────────────────────────────────────
      // Lift the persisted bot flag up to AppDetailPage
      // so Credentials sees fresh state immediately.
      // ─────────────────────────────────────────────
      if (onAppDataPatch) {
        onAppDataPatch({
          live_agent_enabled: botContent.live_agent_enabled,
          pending_timeout_in_minute: botContent.pending_timeout_in_minute,
          tenant_load_threshold: botContent.tenant_load_threshold,
          guest_live_chat_enabled: botContent.guest_live_chat_enabled,
          // ⭐ Nested inside live_settings, so we have to patch the whole
          //    live_settings object (the parent state in AppDetailPage is
          //    a shallow spread, so we can't patch a nested key directly).
          live_settings: botContent.live_settings,
          // ⭐ Top-level. Also needs to be patched so AppDetailPage's
          //    shallow spread sees the fresh value.
          identity_storage: botContent.identity_storage,
        });
      }

      showAlert("success", "Live Agent settings updated successfully!");

      if (window.MyPackage?.processHardReset) {
        window.MyPackage.processHardReset(
          token,
          appData.app_name,
          appData.region.toLowerCase(),
        );
      }
    } catch (error) {
      console.error("Update failed:", error);
      showAlert("error", "Update failed: " + error.message);
    } finally {
      setUpdating(false);
      setPendingUpdatePayloads({ payload1: null });
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
  };

  // ========== VALIDATION FUNCTION FOR CHATBOT STATES ==========
  const checkOtherChatbotsEnabled = () => {
    // Check if AI Chat is enabled
    const isAiAgentEnabled = botContent?.ai_agent_bot_enabled === true;
    // Check if LeadGen Bot (QnA) is enabled
    const isLeadGenEnabled = botContent?.enabled_bot_content === true;

    return {
      aiAgentEnabled: isAiAgentEnabled,
      leadGenEnabled: isLeadGenEnabled,
      anyOtherEnabled: isAiAgentEnabled || isLeadGenEnabled,
    };
  };
  // ========== END VALIDATION FUNCTION ==========

  useEffect(() => {
    if (!appData?.tenant_id || !appData?.app_name) return;

    const initialBotContent = {
      ...appData,
      live_settings: {
        // Start from whatever the app already had (or the message
        // defaults), then layer video_setting on top so an older
        // app without it gets the defaults seeded on first load.
        WELCOME_MSG: appData.live_settings?.WELCOME_MSG || "",
        OFFLINE_MSG: appData.live_settings?.OFFLINE_MSG || "",
        video_setting: {
          audio:
            appData.live_settings?.video_setting?.audio ??
            LIVE_AGENT_DEFAULTS.video_setting.audio,
          video:
            appData.live_settings?.video_setting?.video ??
            LIVE_AGENT_DEFAULTS.video_setting.video,
        },
      },
      // ⭐ NEW: seed the two threshold fields with defaults when the
      //    app data doesn't yet have them (older tenants). Once the
      //    admin saves, the values become part of the app data and
      //    won't be re-seeded.
      pending_timeout_in_minute:
        appData.pending_timeout_in_minute ??
        LIVE_AGENT_DEFAULTS.pending_timeout_in_minute,
      tenant_load_threshold:
        appData.tenant_load_threshold ??
        LIVE_AGENT_DEFAULTS.tenant_load_threshold,
      guest_live_chat_enabled:
        appData.guest_live_chat_enabled ??
        LIVE_AGENT_DEFAULTS.guest_live_chat_enabled,
      // ⭐ NEW: identity storage backend config. Seeded with
      //    defaults so older apps get a well-formed object on
      //    first load; the admin can then override per type.
      identity_storage: {
        visitor:
          appData.identity_storage?.visitor ??
          LIVE_AGENT_DEFAULTS.identity_storage.visitor,
        customer:
          appData.identity_storage?.customer ??
          LIVE_AGENT_DEFAULTS.identity_storage.customer,
      },
    };

    // Initialize chat_settings with style if not present
    if (!initialBotContent.chat_settings?.chat_opener_style) {
      initialBotContent.chat_settings = {
        ...initialBotContent.chat_settings,
        chat_opener_style: "default",
      };
    }

    console.log(
      "Initialized bot content from appData for Live Agent:",
      initialBotContent,
    );
    setBotContent(initialBotContent);
    setInitialData({
      botContent: { ...initialBotContent },
      chatSettings: { ...initialBotContent.chat_settings },
    });
    setLocalChatSettings(appData.chat_settings);
    setSelectedChatStyle(
      initialBotContent.chat_settings?.chat_opener_style || "default",
    );
    setLoading(false);
  }, [appData]);

  const handleUpdate = async () => {
    if (!botContent) {
      showAlert("error", "No Live Agent content to update");
      return;
    }

    // ========== VALIDATION: Check if trying to disable all chatbots ==========
    const isLiveAgentEnabled = botContent.live_agent_enabled;
    const { aiAgentEnabled, leadGenEnabled, anyOtherEnabled } =
      checkOtherChatbotsEnabled();

    // If Live Agent is being disabled (or is already disabled) AND no other chatbots are enabled
    if (!isLiveAgentEnabled && !anyOtherEnabled) {
      showAlert(
        "error",
        "❌ Cannot disable Live Agent Bot! You need at least one chatbot enabled. Please enable either AI Chat or QnA (LeadGen) Bot before disabling Live Agent.",
      );
      return;
    }

    // Also check if we're about to save with Live Agent disabled and no other bots enabled
    // (This handles the case where Live Agent is currently enabled but we're about to disable it)
    const willBeDisabled = !isLiveAgentEnabled;
    if (willBeDisabled && !anyOtherEnabled) {
      showAlert(
        "error",
        "❌ Cannot disable Live Agent Bot! You need at least one chatbot enabled. Currently, both AI Chat and QnA Bot are disabled. Please enable one of them first.",
      );
      return;
    }
    // ========== END VALIDATION ==========

    // ⭐ NEW: validate the two threshold fields are within range
    //    before we let the user proceed to the confirmation modal.
    const pto = Number(botContent.pending_timeout_in_minute);
    if (
      !Number.isFinite(pto) ||
      pto < PENDING_TIMEOUT_MIN ||
      pto > PENDING_TIMEOUT_MAX
    ) {
      showAlert(
        "error",
        `Pending timeout must be a number between ${PENDING_TIMEOUT_MIN} and ${PENDING_TIMEOUT_MAX} minutes.`,
      );
      return;
    }

    const tlt = Number(botContent.tenant_load_threshold);
    if (
      !Number.isFinite(tlt) ||
      tlt < TENANT_LOAD_THRESHOLD_MIN ||
      tlt > TENANT_LOAD_THRESHOLD_MAX
    ) {
      showAlert(
        "error",
        `Tenant load threshold must be a number between ${TENANT_LOAD_THRESHOLD_MIN} and ${TENANT_LOAD_THRESHOLD_MAX}.`,
      );
      return;
    }

    const payload1 = {};
    Object.keys(botContent).forEach((key) => {
      if (CORE_KEYS.includes(key)) payload1[key] = botContent[key];
    });

    console.log("Live Agent Payload:", JSON.stringify(payload1, null, 2));
    console.log(
      "Chat opener style being saved:",
      payload1.chat_settings?.chat_opener_style,
    );
    console.log(
      "Thresholds being saved:",
      payload1.pending_timeout_in_minute,
      payload1.tenant_load_threshold,
    );

    setPendingUpdatePayloads({ payload1 });
    setShowConfirmModal(true);
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, show: false });
  };

  const handleModalClose = () => {
    setShowConfirmModal(false);
  };

  // Preview component for selected style
  const ChatStylePreview = ({ styleKey }) => {
    const style = CHAT_MODAL_STYLES[styleKey];
    if (!style) return null;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: style.previewHtml }} />
      </div>
    );
  };

  if (loading) {
    return (
      <div className={LeadGenBotSettingStyle.centered}>
        <LoaderComp message="Loading Live Agent Settings" />
      </div>
    );
  }

  if (!botContent) {
    return <div>Error: Unable to load Live Agent content</div>;
  }

  return (
    <div className={LeadGenBotSettingStyle.botContent_wrap}>
      <h2 className={LeadGenBotSettingStyle.title}>Live Agent Settings</h2>

      <LiveBot setBotContent={setBotContent} botContent={botContent} />

      {/* ============================================================
          NEW SECTION: Live Agent Queue Thresholds
          ============================================================
          These two fields control the client-side behaviour when a
          user is waiting for a live agent to accept their request.
          The client SDK reads them from the app data.

            • Pending timeout: how long the client waits before
              checking the tenant's overall agent load. If load is
              below the threshold, the client keeps waiting and
              re-checks after another interval.

            • Tenant load threshold: the total number of pending
              + connected sessions at which the client gives up and
              shows "all agents are busy, please try again later."

          ⭐ GATED on `live_agent_enabled` — same condition LiveBot.js
             uses for its own settings section. Rendered inline here
             (rather than inside LiveBot.js) so the sibling layout
             stays exactly as before, but only shown when the live
             agent is actually turned on. This mirrors the behavior
             of every other live-agent-scoped setting on the page.
          ============================================================ */}
      {botContent.live_agent_enabled && (
        <div
          style={{
            marginTop: "32px",
            padding: "20px",
            border: "1px solid #e5e5e5",
            borderRadius: "8px",
            background: "#fafafa",
          }}
        >
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#333",
            }}
          >
            Queue Thresholds
          </h3>
          <p
            style={{
              margin: "0 0 20px 0",
              fontSize: "13px",
              color: "#666",
              lineHeight: "1.5",
            }}
          >
            Control how long a client waits for a live agent and when
            they should be told that all agents are busy. These values
            are read by the chat widget at load time.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
            }}
          >
            {/* Pending timeout */}
            <div>
              <label
                htmlFor="pending_timeout_in_minute"
                style={{
                  display: "block",
                  fontWeight: "600",
                  marginBottom: "6px",
                  fontSize: "14px",
                  color: "#333",
                }}
              >
                Pending Timeout (minutes)
              </label>
              <input
                id="pending_timeout_in_minute"
                type="number"
                min={PENDING_TIMEOUT_MIN}
                max={PENDING_TIMEOUT_MAX}
                step="1"
                value={botContent.pending_timeout_in_minute ?? ""}
                onChange={(e) =>
                  handleNumericFieldChange(
                    "pending_timeout_in_minute",
                    e.target.value,
                    PENDING_TIMEOUT_MIN,
                    PENDING_TIMEOUT_MAX,
                  )
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                  background: "#fff",
                }}
              />
              <p
                style={{
                  margin: "6px 0 0 0",
                  fontSize: "12px",
                  color: "#888",
                  lineHeight: "1.4",
                }}
              >
                How long the client waits after sending a join request
                before checking the tenant&apos;s overall agent load.
                Default: {LIVE_AGENT_DEFAULTS.pending_timeout_in_minute}{" "}
                minutes. Range: {PENDING_TIMEOUT_MIN}–
                {PENDING_TIMEOUT_MAX}.
              </p>
            </div>

            {/* Tenant load threshold */}
            <div>
              <label
                htmlFor="tenant_load_threshold"
                style={{
                  display: "block",
                  fontWeight: "600",
                  marginBottom: "6px",
                  fontSize: "14px",
                  color: "#333",
                }}
              >
                Tenant Load Threshold
              </label>
              <input
                id="tenant_load_threshold"
                type="number"
                min={TENANT_LOAD_THRESHOLD_MIN}
                max={TENANT_LOAD_THRESHOLD_MAX}
                step="1"
                value={botContent.tenant_load_threshold ?? ""}
                onChange={(e) =>
                  handleNumericFieldChange(
                    "tenant_load_threshold",
                    e.target.value,
                    TENANT_LOAD_THRESHOLD_MIN,
                    TENANT_LOAD_THRESHOLD_MAX,
                  )
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                  background: "#fff",
                }}
              />
              <p
                style={{
                  margin: "6px 0 0 0",
                  fontSize: "12px",
                  color: "#888",
                  lineHeight: "1.4",
                }}
              >
                Total pending + connected sessions at which a waiting
                client gives up and sees &quot;all agents are busy.&quot;
                If the current load is below this number, the client
                keeps waiting. Default:{" "}
                {LIVE_AGENT_DEFAULTS.tenant_load_threshold}. Range:{" "}
                {TENANT_LOAD_THRESHOLD_MIN}–{TENANT_LOAD_THRESHOLD_MAX}.
              </p>
            </div>
          </div>
        </div>
      )}
      {/* ============================================================
          END NEW SECTION
          ============================================================ */}

      <button
        onClick={handleUpdate}
        disabled={updating || !botContent || loading || !hasChanges()}
        className={`${LeadGenBotSettingStyle.updateBtn} ${updating ? LeadGenBotSettingStyle.disabled : ""
          }`}
      >
        {updating ? "Updating..." : "Update Live Agent"}
      </button>

      {alert.show && (
        <AlertPopup
          type={alert.type}
          message={alert.message}
          onClose={handleCloseAlert}
        />
      )}

      <Modal
        isOpen={showConfirmModal}
        onClose={handleModalClose}
        hideCloseButton={true}
        title="Confirmation Required"
      >
        <div className={modalContentSty.modalContent}>
          <div className={modalContentSty.modalContentWrap}>
            <div className={modalContentSty.modalContentIc}>⚠️</div>
            <p className={modalContentSty.modalMessage}>
              All the clients would be rebooted! Do you want to continue?
            </p>
          </div>
          <div className={modalContentSty.modalActions}>
            <button
              className={`${modalContentSty.modalButton} ${modalContentSty.cancelBtn}`}
              onClick={() => confirmUpdate(false)}
            >
              Cancel
            </button>
            <button
              className={`${modalContentSty.modalButton} ${modalContentSty.okBtn}`}
              onClick={() => confirmUpdate(true)}
            >
              Ok
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default withPreviewRoot(LiveAgentBotSettings);